import { format } from 'date-fns';
import { Website } from '../../types/website';
import { Button, Card, Icon, IconButton } from '../ui';

interface Link {
  id: string;
  from_id?: string;
  to_id?: string;
  created_at: string;
  website?: Website;
}

interface NetworkConnectionsPanelProps {
  website: {
    outgoingLinks: Link[];
    incomingLinks: Link[];
  } | null;
  onAddLink: () => void;
  onDeleteLink: (linkId: string) => void;
  deletingLinkId: string | null;
}

export default function NetworkConnectionsPanel({
  website,
  onAddLink,
  onDeleteLink,
  deletingLinkId
}: NetworkConnectionsPanelProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  const formatUrl = (url: string) => {
    return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
  };

  const LinkRow = ({ link }: { link: Link }) => {
    const websiteData = link.website;
    const displayUrl = websiteData ? formatUrl(websiteData.url) : 'Unknown website';
    const displayTitle = websiteData?.title || displayUrl;

    return (
      <div
        className="hover-wash flex items-center justify-between"
        style={{
          gap: '12px',
          padding: '8px 10px',
          margin: '0 -10px',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        <div className="flex items-center flex-1" style={{ gap: '10px', minWidth: 0 }}>
          <span style={{ color: 'var(--text-faint)', display: 'inline-flex', flexShrink: 0 }}>
            <Icon name="link" size={14} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center" style={{ gap: '7px' }}>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                  color: 'var(--text-body)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayTitle}
              </span>
              {websiteData && (
                <a
                  href={websiteData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', color: 'var(--text-faint)' }}
                  title="Open Site"
                >
                  <Icon name="arrow-up-right" size={12} />
                </a>
              )}
            </div>
            <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {displayUrl}
            </div>
          </div>
        </div>

        <span className="t-num hidden md:inline" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
          {formatDate(link.created_at)}
        </span>

        {deletingLinkId === link.id ? (
          <span
            className="inline-block w-4 h-4 rounded-full animate-spin"
            style={{ border: '2px solid var(--border-hairline)', borderTopColor: 'var(--accent)', flexShrink: 0 }}
          />
        ) : (
          <IconButton
            icon="trash-2"
            size="sm"
            label="Delete Link"
            onClick={() => onDeleteLink(link.id)}
            disabled={deletingLinkId === link.id}
            style={{ color: 'var(--danger-400)' }}
          />
        )}
      </div>
    );
  };

  const outgoingLinks = website?.outgoingLinks || [];
  const incomingLinks = website?.incomingLinks || [];

  const emptyState = (label: string) => (
    <div className="text-center" style={{ padding: 'var(--space-6) 0', color: 'var(--text-faint)' }}>
      <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}>
        <Icon name="link" size={20} />
      </div>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>{label}</p>
    </div>
  );

  return (
    <Card
      eyebrow="Network Connections"
      actions={
        <Button size="sm" variant="secondary" icon="plus" onClick={onAddLink}>
          Add Link
        </Button>
      }
    >
      {/* Outgoing Links */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <div className="t-eyebrow" style={{ marginBottom: '8px' }}>
          Outgoing · {outgoingLinks.length}
        </div>
        {outgoingLinks.length > 0 ? (
          <div className="max-h-64 overflow-y-auto" style={{ display: 'flex', flexDirection: 'column' }}>
            {outgoingLinks.map((link) => (
              <LinkRow key={link.id} link={link} />
            ))}
          </div>
        ) : (
          emptyState('No outgoing links')
        )}
      </div>

      {/* Incoming Links */}
      <div>
        <div className="t-eyebrow" style={{ marginBottom: '8px' }}>
          Incoming · {incomingLinks.length}
        </div>
        {incomingLinks.length > 0 ? (
          <div className="max-h-64 overflow-y-auto" style={{ display: 'flex', flexDirection: 'column' }}>
            {incomingLinks.map((link) => (
              <LinkRow key={link.id} link={link} />
            ))}
          </div>
        ) : (
          emptyState('No incoming links')
        )}
      </div>
    </Card>
  );
}
